import React, { createContext, useState, useEffect, useContext } from 'react';
import { supabase } from '../services/supabaseClient';
import { TERMS_VERSION } from '../App';

const AuthContext = createContext(null);

/**
 * @typedef {Object} AuthUser
 * @property {string} id
 * @property {string} username
 * @property {string} email
 * @property {'admin' | 'user' | 'owner' | 'moderator'} role
 * @property {'free' | 'premium' | 'premium_plus' | 'lifetime'} subscription_tier
 * @property {'free' | 'paid' | 'tester'} tier
 */

/**
 * @typedef {Object} AuthContextType
 * @property {AuthUser | null} currentUser
 * @property {boolean} isAuthenticated
 * @property {boolean} isLoadingAuth
 * @property {(user: AuthUser, session: any) => void} login
 * @property {() => void} logout
 * @property {(user: AuthUser) => void} updateUser
 * @property {any} supabase
 */

/**
 * @returns {AuthContextType}
 */
export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [userOrgs, setUserOrgs] = useState([]);
  const [token, setToken] = useState(null);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [orgMemberships, setOrgMemberships] = useState([]); // [{ org_id, role, org: { id, name } }]
  const [mfaStatus, setMfaStatus] = useState(null); // { mfaEnabled, enforcementLevel }
  const [mfaVerifiedThisSession, setMfaVerifiedThisSession] = useState(false);

  // Fetch MFA status for a given access token (retries on failure)
  const fetchMfaStatus = async (accessToken) => {
    if (!accessToken) return;
    const maxRetries = 2;
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL || ''}/api/mfa/status`,
          { headers: { 'Authorization': `Bearer ${accessToken}` } }
        );
        if (response.ok) {
          const data = await response.json();
          setMfaStatus({
            mfaEnabled: data.mfaEnabled || false,
            enforcementLevel: data.enforcementLevel || 'sensitive_only',
          });
          return;
        }
      } catch {
        if (attempt < maxRetries) {
          await new Promise(r => setTimeout(r, 1000 * (attempt + 1)));
          continue;
        }
      }
    }
    // After retries exhausted, backend requireMFA() middleware still protects sensitive ops
  };

  // Handle user session
  // PRD-037 SECURITY FIX: Removed session/token logging to prevent information exposure
  const handleUserSession = async (session) => {
    if (!session?.user) {
      // PRD-037: Only log non-sensitive status in development
      if (process.env.NODE_ENV === 'development') {
        console.log('[AuthContext] No session user, setting currentUser to null');
      }
      setCurrentUser(null);
      setOrgMemberships([]);
      return;
    }

    // PRD-037 SECURITY FIX: NEVER log session objects - they contain access tokens

    try {
      let userProfileData = null;

      // Try direct profile query first with better error handling
      try {
        const { data: directProfile, error: directError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', session.user.id)
          .single();

        if (directProfile && !directError) {
          userProfileData = directProfile;
          // PRD-037: Removed profile data logging - only log in dev if needed for debugging
        } else {
          throw new Error(directError?.message || 'Direct profile query failed');
        }
      } catch (directQueryError) {
        console.warn('[AuthContext] Direct profile query failed, trying RPC.', directQueryError?.message);

        // If direct query failed, try function approach
        try {
          const { data: rpcProfile, error: rpcError } = await supabase.rpc('get_my_profile');

          // RPC returns TABLE type — data is an array; extract first row or null
          const rpcRow = Array.isArray(rpcProfile) ? rpcProfile[0] : rpcProfile;
          if (rpcRow && !rpcError) {
            userProfileData = rpcRow;
            // PRD-037: Removed profile data logging
          } else {
            throw new Error(rpcError?.message || 'RPC profile query failed');
          }
        } catch (rpcQueryError) {
          console.error('[AuthContext] get_my_profile function also failed.', rpcQueryError?.message);
          // Profile queries failed - will handle in fallback below
        }
      }

      // Process profile data
      if (userProfileData) {
        // PRD-037: Removed profile data logging
        const finalRole = (userProfileData.role || session.user.user_metadata?.role || 'user').toLowerCase();

        const userProfile = {
          id: session.user.id,
          username: userProfileData.username || session.user.user_metadata?.username || 'Unknown',
          email: session.user.email,
          role: finalRole,
          tier: userProfileData.tier || 'free',
          subscription_tier: userProfileData.subscription_tier || 'free',
          whitelisted: !!userProfileData.whitelisted,
          org_entitled: !!userProfileData.org_entitled,
          orgId: userProfileData.org_id || null,
          dev_access: !!userProfileData.dev_access,
          beta_access: !!userProfileData.beta_access,
          created_at: userProfileData.created_at || session.user.created_at,
          updated_at: userProfileData.updated_at || session.user.updated_at,
          terms_version: userProfileData.terms_version || 0,
          terms_accepted_at: userProfileData.terms_accepted_at || null,
          dm_enabled: userProfileData.dm_enabled !== false, // Default true
          dm_notifications_enabled: userProfileData.dm_notifications_enabled !== false, // Default true
          dm_auto_popup: userProfileData.dm_auto_popup !== false, // Default true
          // PRD-079: SC Handle verification state
          star_citizen_name: userProfileData.star_citizen_name || null,
          sc_handle_verified: !!userProfileData.sc_handle_verified,
          // PRD-067: Subscription & trial state fields
          subscription_status: userProfileData.subscription_status || 'inactive',
          trial_used: !!userProfileData.trial_used,
          subscription_lapsed_at: userProfileData.subscription_lapsed_at || null,
          data_wipe_scheduled_at: userProfileData.data_wipe_scheduled_at || null,
        };

        setCurrentUser(userProfile);
        // PRD-037: Removed user profile logging - contains sensitive data

        // Fetch MFA status for enforcement gate
        fetchMfaStatus(session.access_token);

        // Load org memberships for nav/UI with improved error handling
        try {
          const { data: memberships, error: orgError } = await supabase
            .from('org_members')
            .select('org_id')
            .eq('user_id', session.user.id);

          if (memberships && !orgError) {
            setOrgMemberships(Array.isArray(memberships) ? memberships : []);
          } else {
            console.warn('[AuthContext] Org membership query failed, trying fallback RPC:', orgError?.message);

            // Try fallback RPC function
            try {
              const { data: rpcMemberships, error: rpcOrgError } = await supabase
                .rpc('get_user_org_roles', { target_user_id: session.user.id });

              if (rpcMemberships && !rpcOrgError) {
                setOrgMemberships(Array.isArray(rpcMemberships) ? rpcMemberships : []);
              } else {
                console.warn('[AuthContext] RPC org membership query also failed:', rpcOrgError?.message);
                // For privileged users, assume they have org access if other queries failed
                if (userProfile.role === 'owner' || userProfile.role === 'admin') {
                  console.log('[AuthContext] Privileged user detected, assuming org access');
                  setOrgMemberships([{ org_id: null, role: 'admin' }]);
                } else {
                  setOrgMemberships([]);
                }
              }
            } catch (rpcOrgError) {
              console.error('[AuthContext] RPC org membership query failed:', rpcOrgError?.message);
              setOrgMemberships([]);
            }
          }
        } catch (orgMembershipError) {
          console.error('[AuthContext] Error loading org memberships:', orgMembershipError);
          setOrgMemberships([]);
        }
      } else {
        // Profile not found — could be a brand-new signup (trigger hasn't fired yet)
        // or a stale session for a deleted account. Check if the account was created
        // more than 30 seconds ago; if so, the profile should exist and this is stale.
        const createdAt = new Date(session.user.created_at).getTime();
        const isNewSignup = (Date.now() - createdAt) < 30_000;

        if (!isNewSignup) {
          console.warn('[AuthContext] Profile missing for existing user — signing out stale session');
          try { await supabase.auth.signOut(); } catch (_) {}
          setCurrentUser(null);
          setToken(null);
          setOrgMemberships([]);
          return;
        }

        // PRD-037: Reduced logging - no sensitive data
        const userEmail = session.user.email;
        const userMetaUsername = session.user.user_metadata?.username;

        // Create basic user profile with default 'user' role
        // Trust ONLY database values - never assign privileges client-side
        const basicUser = {
          id: session.user.id,
          username: userMetaUsername || session.user.email?.split('@')[0] || 'Unknown',
          email: userEmail,
          role: 'user', // Default to 'user' role - privileges must come from database
          tier: 'free',
          subscription_tier: 'free',
          whitelisted: false,
          org_entitled: false,
          orgId: null,
          dev_access: false,
          beta_access: false,
          created_at: session.user.created_at,
          updated_at: session.user.updated_at,
          terms_version: 0,
          terms_accepted_at: null,
        };
        setCurrentUser(basicUser);
        // PRD-037: Removed basic user logging
        setOrgMemberships([]);
      }

    } catch (error) {
      console.error('[AuthContext] Error in handleUserSession:', error);
      setCurrentUser(null);
      setOrgMemberships([]);
    }
  };

  // Initialize auth state
  useEffect(() => {
    let fallbackTimeout;

    // Get initial session
    const getInitialSession = async () => {
      try {
        // PRD-037: Removed session logging
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('[AuthContext] Error getting initial session:', error);
          // Handle refresh token / invalid session errors gracefully
          const msg = error.message || '';
          if (msg.includes('Refresh Token') || msg.includes('Invalid') || msg.includes('not found') || msg.includes('expired')) {
            console.warn('[AuthContext] Session invalid, signing out');
            try {
              await supabase.auth.signOut();
            } catch (signOutError) {
              console.error('[AuthContext] Error signing out:', signOutError);
            }
            setCurrentUser(null);
            setToken(null);
          }
        }
        
        if (session) {
          // PRD-037: Session found - do not log session details
          // Set the access token for API calls
          setToken(session.access_token);
          await handleUserSession(session);
        }
      } catch (error) {
        console.error('[AuthContext] Error in getInitialSession:', error);
        // If there's a session error, clear the session so user can log in fresh
        const msg = error?.message || '';
        if (msg.includes('Refresh Token') || msg.includes('Invalid') || msg.includes('not found') || msg.includes('expired')) {
          try {
            await supabase.auth.signOut();
          } catch (signOutError) {
            console.error('[AuthContext] Error signing out:', signOutError);
          }
          setCurrentUser(null);
          setToken(null);
        }
      } finally {
        // PRD-037: Reduced logging
        setIsLoading(false);
        if(fallbackTimeout) clearTimeout(fallbackTimeout);
      }
    };

    getInitialSession();

    // This fallback timeout is a safeguard. With the performance fixes, it might not be necessary,
    // but it's kept to prevent the UI from getting stuck in a loading state on slower connections.
    fallbackTimeout = setTimeout(() => {
      // PRD-037: Reduced logging
      setIsLoading(false);
    }, 5000); 

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        // PRD-037 SECURITY FIX: NEVER log session - only log event type in dev
        if (process.env.NODE_ENV === 'development') {
          console.log('[AuthContext] Auth state change:', event);
        }
        if ((event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION') && session) {
          // Set the access token for API calls
          setToken(session.access_token);

          if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') {
            // Avoid reentering getInitialSession path and unblock UI immediately
            try { if (fallbackTimeout) clearTimeout(fallbackTimeout); } catch {}
            setIsLoading(false);
            if (event === 'SIGNED_IN') {
              try {
                // Run in background; UI will update when ready
                handleUserSession(session);
              } catch (_) {}
            }
          }
        } else if (event === 'SIGNED_OUT') {
          setCurrentUser(null);
          setUserOrgs([]);
          setOrgMemberships([]);
          setToken(null);
          setMfaStatus(null);
          setMfaVerifiedThisSession(false);
          setIsLoading(false);
          try { if (fallbackTimeout) clearTimeout(fallbackTimeout); } catch {}
        }
      }
    );

    return () => {
      subscription.unsubscribe();
      if(fallbackTimeout) clearTimeout(fallbackTimeout);
    };
  }, []);

  useEffect(() => {
    if (currentUser) {
        const userTermsVersion = currentUser?.terms_version || 0;
        const needsTerms = userTermsVersion < TERMS_VERSION;
        
        if (needsTerms) {
          setShowTermsModal(true);
        } else {
          setShowTermsModal(false);
        }
    }
  }, [currentUser]);

  const hasAnyOrgMembership = orgMemberships && orgMemberships.length > 0;
  const preferredOrgId = currentUser?.orgId || (hasAnyOrgMembership ? orgMemberships[0].org_id : null);

  const login = async () => {
    // login logic is handled by UI components calling supabase.auth.signInWith...
    // onAuthStateChange will handle the session.
  };

  const logout = async () => {
    // PRD-037 Task 3.2 & 3.7: Clear all user-specific data on logout
    if (currentUser) {
      localStorage.removeItem(`terms_accepted_${currentUser.id}`);
      localStorage.removeItem(`wallet_${currentUser.id}`);
      localStorage.removeItem(`last_activity_${currentUser.id}`);
    }

    // PRD-037: Clear temporary registration data
    localStorage.removeItem('tempUsername');

    // PRD-037: Clear any legacy token storage
    localStorage.removeItem('token');
    sessionStorage.removeItem('token');

    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('Error signing out:', error);
      }
    } catch (error) {
      // Handle AuthSessionMissingError - session already gone, just clear local state
      console.warn('[AuthContext] Sign out error (session may already be invalid):', error.message);
    }

    // Always clear local state regardless of signOut result
    // This ensures logout works even if session is already invalid
    setCurrentUser(null);
    setToken(null);
    setShowTermsModal(false);
    setMfaStatus(null);
    setMfaVerifiedThisSession(false);
  };

  const updateUser = (updatedUserData) => {
    setCurrentUser(prevUser => ({...prevUser, ...updatedUserData}));
  };

  const acceptLatestTerms = async () => {
    if (!currentUser) {
      console.error('[AuthContext] No current user to accept terms for');
      return;
    }

    setShowTermsModal(false);

    // Create timestamp for when terms were accepted (for legal proof)
    const acceptedAt = new Date().toISOString();

    try {
      const { error } = await supabase.from('profiles').update({
        terms_version: TERMS_VERSION,
        terms_accepted_at: acceptedAt
      }).eq('id', currentUser.id);

      if (!error) {
        console.log('[AuthContext] Terms accepted successfully at', acceptedAt);
        updateUser({ terms_version: TERMS_VERSION, terms_accepted_at: acceptedAt });
      } else {
        console.error('[AuthContext] Error accepting terms:', error);
        // optimistically update UI
        updateUser({ terms_version: TERMS_VERSION, terms_accepted_at: acceptedAt });
      }
    } catch (error) {
      console.error('[AuthContext] Exception in acceptLatestTerms:', error);
      // optimistically update UI
      updateUser({ terms_version: TERMS_VERSION, terms_accepted_at: acceptedAt });
    }
  };

  // Function to refresh user profile from database
  // PRD-037: Removed sensitive logging
  const refreshUserProfile = async (userId = null) => {
    const targetId = userId || currentUser?.id;
    if (!targetId) return;

    try {
      const { data: freshProfile, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', targetId)
        .single();

      if (error) throw error;

      // Update current user context if refreshing own profile
      if (targetId === currentUser?.id) {
        const updatedUser = {
          ...currentUser,
          role: freshProfile.role,
          tier: freshProfile.tier,
          subscription_tier: freshProfile.subscription_tier || 'free',
          whitelisted: freshProfile.whitelisted,
          updated_at: freshProfile.updated_at,
          // PRD-067: Subscription & trial state fields
          subscription_status: freshProfile.subscription_status || 'inactive',
          trial_used: !!freshProfile.trial_used,
          subscription_lapsed_at: freshProfile.subscription_lapsed_at || null,
          data_wipe_scheduled_at: freshProfile.data_wipe_scheduled_at || null,
        };

        setCurrentUser(updatedUser);

        // Refresh org memberships so nav reflects any new/removed orgs
        try {
          const { data: memberships, error: orgError } = await supabase
            .from('org_members')
            .select('org_id')
            .eq('user_id', targetId);
          if (memberships && !orgError) {
            setOrgMemberships(Array.isArray(memberships) ? memberships : []);
          }
        } catch (_) {}
      }

      return freshProfile;
    } catch (error) {
      console.error('[AuthContext] Failed to refresh profile:', error);
      throw error;
    }
  };

  // Add role/tier/whitelist helpers for premium access
  // tier: 'paid' (Grant Paid tool), 'tester' (testers), 'free' (default)
  // role: 'owner', 'admin', 'moderator', 'user'
  // whitelisted: boolean for early access users
  const isAdmin = currentUser?.role === 'admin';
  const isModerator = currentUser?.role === 'moderator';
  const isOwner = currentUser?.role === 'owner';
  const isPaid = currentUser?.tier === 'paid';
  const isTester = currentUser?.tier === 'tester';
  const isWhitelisted = !!currentUser?.whitelisted;

  // PRD-067: Trial and subscription status helpers
  const subscriptionTier = currentUser?.subscription_tier || 'free';
  const subscriptionStatus = currentUser?.subscription_status || 'inactive';
  const isTrialing = subscriptionStatus === 'trialing';
  const isLapsed = subscriptionStatus === 'lapsed';
  const isExpiredTrial = subscriptionStatus === 'expired_trial';
  const trialEligible = !currentUser?.trial_used && !isPaid && !isTrialing && subscriptionTier === 'free'
    && !isAdmin && !isModerator && !isOwner && !isWhitelisted && !isTester;
  const isSubscriptionActive = ['active', 'trialing', 'past_due'].includes(subscriptionStatus) && ['premium', 'premium_plus', 'lifetime'].includes(subscriptionTier);
  const dataWipeDate = currentUser?.data_wipe_scheduled_at || null;

  // PRD-067: Enhanced premium access - includes trial users and active subscribers
  const hasPremiumAccess = isPaid || isTester || isWhitelisted || isAdmin || isModerator || isOwner || isTrialing || isSubscriptionActive;

  const value = {
    currentUser,
    login,
    logout,
    updateUser,
    refreshUserProfile,
    isAuthenticated: !!currentUser,
    isLoadingAuth: isLoading,
    userOrgs,
    supabase,
    token, // Deprecated legacy JWT token
    getAccessToken: async () => {
      try {
        const { data } = await supabase.auth.getSession();
        return data?.session?.access_token || null;
      } catch {
        return null;
      }
    },
    showTermsModal,
    acceptLatestTerms,
    // Role/tier helpers
    isAdmin,
    isModerator,
    isOwner,
    isPaid,
    isTester,
    isWhitelisted,
    hasPremiumAccess,
    // PRD-067: Trial and subscription state
    isTrialing,
    isLapsed,
    isExpiredTrial,
    trialEligible,
    isSubscriptionActive,
    subscriptionTier,
    subscriptionStatus,
    dataWipeDate,
    orgMemberships,
    hasAnyOrgMembership,
    preferredOrgId,
    // MFA enforcement state
    mfaStatus,
    mfaVerifiedThisSession,
    setMfaVerifiedThisSession,
    fetchMfaStatus,
  };
  
  // PRD-037 SECURITY FIX: REMOVED window debugging functions
  // These exposed token accessors that could be exploited by XSS attacks
  // DO NOT re-enable - use React DevTools for debugging instead
  
  return (
    <AuthContext.Provider value={value}>
      {!isLoading && children}
    </AuthContext.Provider>
  );
};
